import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { licenceApi } from "../../services/licence.api";
import { formatDateTime } from "../../utils/date-format-utils";
import { licenceValueLabel } from "../../utils/enum-label-utils";
import { FacilityLicenceSummary } from "../../types/licence.definition";
import { DoctorLicencesManagement } from "./DoctorLicencesManagement";

type Tab = "doctors" | "facilities";

const STATUS_BADGE: Record<string, string> = {
  ACTIVE: "bg-green-100 text-green-700",
  PAUSED: "bg-yellow-100 text-yellow-700",
  EXPIRED: "bg-red-100 text-red-700",
};

export const LicencesManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>("doctors");
  const [facilityLicences, setFacilityLicences] = useState<
    FacilityLicenceSummary[]
  >([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [facilitiesError, setFacilitiesError] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab === "facilities" && facilityLicences.length === 0) {
      setFacilitiesLoading(true);
      setFacilitiesError(null);
      licenceApi
        .getAllFacilityLicences()
        .then(data => setFacilityLicences(data))
        .catch(() =>
          setFacilitiesError(t("licencesManagement.facilities.errorLoading"))
        )
        .finally(() => setFacilitiesLoading(false));
    }
  }, [activeTab, facilityLicences.length, t]);

  return (
    <div className="page-container">
      {/* Page header */}
      <div className="page-header">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("admin.tabs.licences")}
          </h2>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab("doctors")}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "doctors"
              ? "border-primary-600 text-primary-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          {t("licencesManagement.tabDoctors")}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("facilities")}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "facilities"
              ? "border-primary-600 text-primary-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          {t("licencesManagement.tabFacilities")}
        </button>
      </div>

      {/* Doctors tab */}
      {activeTab === "doctors" && <DoctorLicencesManagement />}

      {/* Facilities tab */}
      {activeTab === "facilities" && (
        <div>
          <p className="text-sm text-gray-500 mb-6">
            {t("licencesManagement.facilities.subtitle")}
          </p>

          {facilitiesError && (
            <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {facilitiesError}
            </div>
          )}

          {facilitiesLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
          ) : facilityLicences.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              {t("licencesManagement.facilities.noFacilities")}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      {t("licencesManagement.facilities.facilityName")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("licencesManagement.facilities.licenceStatus")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("licencesManagement.facilities.licenceType")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("licencesManagement.facilities.endDate")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("licencesManagement.facilities.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {facilityLicences.map(lic => (
                    <tr
                      key={lic.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() =>
                        lic.facilityId &&
                        navigate(`/admin/facilities/${lic.facilityId}`)
                      }
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {lic.facilityName ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        {lic.status ? (
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[lic.status] ?? "bg-gray-100 text-gray-600"}`}
                          >
                            {t(`doctorLicence.section.statuses.${lic.status}`)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      {/* MED-1543: this column printed the API enum
                          ("STANDARD", "BASIC") while every other licence
                          screen shows the label from the one set that holds
                          them (admin.invoices.values, see licenceValueLabel). */}
                      <td className="px-4 py-3 text-gray-700">
                        {lic.licenceType
                          ? licenceValueLabel(t, lic.licenceType)
                          : "—"}
                      </td>
                      {/* MED-1543: and the end date as a raw "2031-07-26". */}
                      <td className="px-4 py-3 text-gray-600">
                        {formatDateTime(lic.endDate)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          className="text-xs px-3 py-1.5 rounded border border-primary-300 text-primary-600 hover:bg-primary-50"
                          onClick={e => {
                            e.stopPropagation();
                            if (lic.facilityId) {
                              navigate(`/admin/facilities/${lic.facilityId}`);
                            }
                          }}
                        >
                          {t("licencesManagement.facilities.viewLicence")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
